import type {
  GlycerinFilter,
  GlycerinProductListBasePayload,
  GlycerinDisplayProductListPayload,
  GlycerinDisplaySearchResultPayload,
  GlycerinTile,
  GTMEventGlycerinDisplayProductList,
  GTMEventGlycerinDisplaySearchResult,
} from '@packages/tracking';
import {
  GlycerinFilterType,
  GlycerinSortMethod,
  useTracking,
  GlycerinTileType,
} from '@packages/tracking';

import { useRouter } from 'next/router';

import type { DesktopDisplayMode } from '../../types';
import type { ActualSearchProductCardProps } from '../SearchProductCard';
import type { CriteoProduct, CurrentNavigation, SelectedFilter } from '../../api/rest/types';
import { decodeSharedQueryParamsForRestApi } from '../../queryEncoding';

/**
 * Maps the given filter type to its corresponding glycerin filter type value.
 *
 * NOTE: currently only RANGE and MULTISELECT filter types are supported due to the circumstance that no single select filters are being used.
 * If a single select filter is being used this function needs to be refactored to support single select filters.
 */
const mapFilterTypeToGlycerinFilterType = (
  typeToMap: 'patch' | 'list' | 'slider',
): GlycerinFilterType =>
  typeToMap === 'slider' ? GlycerinFilterType.RANGE : GlycerinFilterType.MULTISELECT;

/**
 * Maps the given product to a GlycerinTile.
 */
const mapProductToGlycerinTile = (
  product: ActualSearchProductCardProps | CriteoProduct,
): GlycerinTile => ({
  productId:
    'criteo' in product ? product.variations[0].artNo : product.product.variations[0].artNo,
  articleId: 'criteo' in product ? product.masterSku : product.product.masterSku,
  skuId: 'criteo' in product ? product.variations[0].sku : product.product.variations[0].sku,
  listPosition: 'criteo' in product ? product.productPosition : product.product.productPosition,
  tileType: 'criteo' in product ? GlycerinTileType.AD : GlycerinTileType.PRODUCT,
  adId: 'criteo' in product ? 'criteo' : undefined,
});

/**
 * Maps the sorting method to its corresponding glycerin enum value.
 * Possible methods: relevance,price-asc,price-desc,novelty,brand-asc,brand-desc,ratings
 */
const mapSortingMethod = (method: string): GlycerinSortMethod => {
  switch (method) {
    case 'price-asc':
      return GlycerinSortMethod.PRICEASCENDING;
    case 'price-desc':
      return GlycerinSortMethod.PRICEDESCENDING;
    case 'ratings':
      return GlycerinSortMethod.RATING;
    case 'relevance':
      return GlycerinSortMethod.POPULARITY;
    default:
      return GlycerinSortMethod.OTHER;
  }
};

type DispatchProductListTrackingProps = {
  pageTitle: string;
  products: ActualSearchProductCardProps[];
  pageNumber?: number;
  count: number;
  sortType: string;
  selectedFilters: SelectedFilter[];
  displayMode?: DesktopDisplayMode;
  criteoProducts?: CriteoProduct[];
  alternativeQuery?: string;
  resultFallbackType?: 'DeepQuery' | 'QueryReduction';
  pageType: 'serp' | 'productlist';
  categoryDataForTracking: {
    breadcrumbData?: CurrentNavigation[];
    currentCategoryName?: string;
  };
  query?: string;
};

export const useProductListTracking = () => {
  const dispatchGTMEvent = useTracking();
  const { query: urlQuery } = useRouter();
  const { searchHistory, ssuggest } = decodeSharedQueryParamsForRestApi(urlQuery);

  const dispatchProductListTracking = (props: DispatchProductListTrackingProps) => {
    const {
      count,
      pageNumber,
      pageTitle,
      products,
      selectedFilters,
      sortType,
      alternativeQuery,
      criteoProducts,
      displayMode,
      resultFallbackType,
      pageType,
      categoryDataForTracking,
      query,
    } = props;

    // do not send event if no products available
    if (products.length < 1) {
      return;
    }

    // map criteo products (if available)
    let mappedCriteoProducts: GlycerinTile[] = [];
    if (criteoProducts) {
      mappedCriteoProducts = criteoProducts?.map((criteoProduct) =>
        mapProductToGlycerinTile(criteoProduct),
      );
    }

    // map "regular" products
    const mappedProducts: GlycerinTile[] = products.map((product) =>
      mapProductToGlycerinTile(product),
    );

    // map selected filters
    const filters: GlycerinFilter[] = selectedFilters.map((selectedFilter) => ({
      filterType: mapFilterTypeToGlycerinFilterType(selectedFilter.type),
      name: selectedFilter.name,
      value:
        'slider' in selectedFilter
          ? [
              selectedFilter.slider.minSelected?.toFixed() ?? '',
              selectedFilter.slider.maxSelected?.toFixed() ?? '',
            ]
          : selectedFilter.groupedValues
              .map((groupedValue) => groupedValue.values.map((value) => value.name))
              .flat(1),
    }));

    // create base payload used by both product list and search result events
    const basePayload: GlycerinProductListBasePayload = {
      // criteo products are added first because they are displayed on top of the regular products if available
      tiles: [mappedCriteoProducts, mappedProducts].flat(1),
      // if displayType is undefined use P for cards as default
      listType: displayMode,
      pageNumber: pageNumber ?? 1,
      sortMethod: mapSortingMethod(sortType),
      totalSize: count,
      filters,
    };

    // send product list payload
    if (pageType === 'productlist') {
      const payload: GlycerinDisplayProductListPayload = {
        ...basePayload,
        name: pageTitle,
        categories: categoryDataForTracking?.breadcrumbData?.map(
          (breadCrumbDataItem) => breadCrumbDataItem.name,
        ),
      };
      dispatchGTMEvent<GTMEventGlycerinDisplayProductList>({
        event: 'DisplayProductList',
        DisplayProductListData: payload,
      });
    }
    // send search result payload
    else if (pageType === 'serp') {
      let searchType = 'normal';
      if (searchHistory) {
        searchType = 'history';
      }
      if (ssuggest) {
        searchType = `${ssuggest}-suggest`;
      }
      const payload: GlycerinDisplaySearchResultPayload = {
        ...basePayload,
        searchTermSubmitted: query ?? '',
        searchTermUtilized: alternativeQuery ?? query ?? '',
        algorithm: resultFallbackType,
        searchType,
      };
      dispatchGTMEvent<GTMEventGlycerinDisplaySearchResult>({
        event: 'DisplaySearchResult',
        DisplaySearchResultData: payload,
      });
    }
  };

  return dispatchProductListTracking;
};
